/**
 * RichChatMessage — Markdown + LaTeX renderer for Socratic chat bubbles.
 *
 * Replaces the old home-rolled `renderMarkdown()` regex (which only handled
 * bold/italic) with the full react-markdown stack. Produces correctly
 * rendered:
 *   - Headings (### Step 1)
 *   - Ordered + unordered lists (- item, 1. item)
 *   - Bold (**...**) and italic (*...*)
 *   - Inline code (`...`) and code blocks (```)
 *   - Block math (\[ ... \] and $$ ... $$)
 *   - Inline math (\( ... \) and $ ... $)
 *   - Equilibrium arrows (\rightleftharpoons), fractions (\frac), sub/sup,
 *     scientific notation (\times 10^{-2}), Greek letters, summations, etc.
 *
 * Malformed LaTeX renders in red (rehype-katex strict=false) instead of
 * crashing. Existing transcripts with raw LaTeX/markdown automatically
 * render correctly on next view — no backfill needed.
 */
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import normalizeMath from '@/lib/normalizeMath';

// Tasteful Tailwind overrides for elements that would otherwise inherit
// browser defaults (h2 too large, lists with no padding, etc.). Tuned to
// match the chat bubble's compact 14px body text.
const COMPONENTS = {
  h1: ({ children, ...p }) => (
    <h1 className="text-base font-bold text-slate-900 mt-2 mb-1.5" {...p}>{children}</h1>
  ),
  h2: ({ children, ...p }) => (
    <h2 className="text-sm font-bold text-slate-900 mt-2 mb-1" {...p}>{children}</h2>
  ),
  h3: ({ children, ...p }) => (
    <h3 className="text-sm font-semibold text-slate-800 mt-2 mb-1" {...p}>{children}</h3>
  ),
  h4: ({ children, ...p }) => (
    <h4 className="text-sm font-semibold text-slate-700 mt-1.5 mb-1" {...p}>{children}</h4>
  ),
  p: ({ children, ...p }) => (
    <p className="my-1.5 leading-relaxed" {...p}>{children}</p>
  ),
  ul: ({ children, ...p }) => (
    <ul className="list-disc pl-5 my-1.5 space-y-0.5" {...p}>{children}</ul>
  ),
  ol: ({ children, ...p }) => (
    <ol className="list-decimal pl-5 my-1.5 space-y-0.5" {...p}>{children}</ol>
  ),
  li: ({ children, ...p }) => (
    <li className="leading-relaxed" {...p}>{children}</li>
  ),
  strong: ({ children, ...p }) => (
    <strong className="font-bold text-slate-900" {...p}>{children}</strong>
  ),
  em: ({ children, ...p }) => (
    <em className="italic" {...p}>{children}</em>
  ),
  code: ({ inline, children, ...p }) => (
    inline ? (
      <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-700 text-[12px] font-mono" {...p}>{children}</code>
    ) : (
      <code className="block p-2 rounded-md bg-slate-100 text-slate-700 text-[12px] font-mono whitespace-pre overflow-x-auto" {...p}>{children}</code>
    )
  ),
  pre: ({ children, ...p }) => (
    <pre className="my-1.5 rounded-md bg-slate-100 overflow-x-auto" {...p}>{children}</pre>
  ),
  blockquote: ({ children, ...p }) => (
    <blockquote className="border-l-2 border-slate-300 pl-3 my-1.5 italic text-slate-600" {...p}>{children}</blockquote>
  ),
  a: ({ children, href, ...p }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-indigo-600 hover:underline"
      {...p}
    >
      {children}
    </a>
  ),
  hr: () => <hr className="my-3 border-slate-200" />,
};

export default function RichChatMessage({ text, className = '' }) {
  if (!text) return null;
  // Normalise the LLM's many LaTeX/math styles to `$...$`/`$$...$$` so
  // remark-math reliably picks them up. Handles bare `\frac`, parens-wrapped
  // LaTeX (`(\frac{a}{b})`), chemistry equations without delimiters, currency
  // escapes, etc.
  const normalised = normalizeMath(text);
  return (
    <div className={`text-sm text-gray-700 leading-relaxed ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[[rehypeKatex, {
          strict: false,
          throwOnError: false,
          errorColor: '#ef4444',
        }]]}
        components={COMPONENTS}
      >
        {normalised}
      </ReactMarkdown>
    </div>
  );
}
